import { useGetRestaurantInfo } from 'api/restaurants';
import useAuth from 'hooks/useAuth';
import { createContext, ReactNode, useContext } from 'react';
import type { RestaurantType } from 'types/restaurant';

interface RestaurantContextType {
  restaurant: RestaurantType | undefined;
  isLoading: boolean;
  error: any;
  currentRestaurantId: number;
  refreshRestaurant: () => void;
}

const RestaurantContext = createContext<RestaurantContextType | undefined>(undefined);

interface RestaurantProviderProps {
  children: ReactNode;
  restaurantId?: number | string | null;
}

export function RestaurantProvider({ children, restaurantId: providedRestaurantId }: RestaurantProviderProps) {
  const { user } = useAuth();
  // Use provided restaurantId if available (for admin routes), otherwise use user's restaurant_ids
  const currentRestaurantId =
    providedRestaurantId !== undefined && providedRestaurantId !== null
      ? Number(providedRestaurantId)
      : Number(user?.restaurant_ids?.[0]) || 0;

  const { restaurant, restaurantLoading, restaurantError, refetch } = useGetRestaurantInfo({
    restaurantId: currentRestaurantId
  });

  const value = {
    restaurant,
    isLoading: restaurantLoading,
    error: restaurantError,
    currentRestaurantId,
    refreshRestaurant: () => {
      // trigger SWR mutate to refetch restaurant info
      refetch();
    }
  };

  return <RestaurantContext.Provider value={value}>{children}</RestaurantContext.Provider>;
}

export function useRestaurantContext() {
  const context = useContext(RestaurantContext);
  if (context === undefined) {
    throw new Error('useRestaurantContext must be used within a RestaurantProvider');
  }
  return context;
}
